import { IServerSideDatasource, RowClickedEvent } from "ag-grid-community";

import {
    Component,
    inject,
    OnInit,
    ViewChild
} from "@angular/core";

import { 
    ConnectionService,
    EntityManagerService,
    PaginatedResponse,
    ToolbarEventsService
} from "src/app/core";

import {
    GridCellExportFormatterService,
    GridCellFormatterService,
    GridClickableCashMovementsButtonComponent,
    GridFixedButtons,
    GridServerSideComponent,
    headersTranslate
} from "src/app/shared/components";

import { from } from "rxjs";
import { CashMovements } from "tilby-models";
import { CashMovementsFe, DataExport, TableData} from "src/app/shared/model/model";

import {
    GridDownloadDialogService
} from "src/app/dialogs";

import { autobind } from "src/app/models/decorators.model";
import { CliccableActionCashMovementscard } from "src/app/shared/components/grid/grid-clickable-button-cash-movements/grid-clickable-button-cash-movements.component";
import { OnDestroyService } from "src/app/core/services/on-destroy.service";
import { CashMovementDialogService } from "src/app/dialogs/cash-movement/cash-movement.dialog.component";
import { subscribeInComponent } from "src/app/shared/angular-utils";

@Component({
	standalone: false,
    selector: "app-history-cash-movements",
    templateUrl: "./history-cash-movements.component.html",
    styleUrls: ["./history-cash-movements.component.scss"],
    providers: [OnDestroyService],
})
export class HistoryCashMovementsComponent implements OnInit {
    private readonly cashMovementDialogService = inject(CashMovementDialogService);
    private readonly entityManager = inject(EntityManagerService);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly connection = inject(ConnectionService);
    private readonly gridCellFormatterService = inject(GridCellFormatterService);
    private readonly gridCellExportFormatterService = inject(GridCellExportFormatterService);
    private readonly gridDownloadDialogService = inject(GridDownloadDialogService);

    @ViewChild("appHistoryCashGridComponent", { static: true }) gridRef!: GridServerSideComponent;
    isSelected = false;
    movColumnsSwitch = false;
    isAdvancedFilterActive = false;
    fixedButtons:GridFixedButtons= {checkbox:{visible:true,lockPosition: 'left'},
    customButton:{visible:true,cellRenderer: GridClickableCashMovementsButtonComponent,lockPosition: 'right'}};
    exportFormatterOptions?: (...any: any[])=> any;

    readonly query: Readonly<any> = {
        page: 0,
        pagination: true,
        per_page: 50,
    };

    datasource: IServerSideDatasource = {
        getRows: (params) =>
            this.gridRef.getRowsForServersideDatasource(
                params,
                [this.query],
                (a, b?) =>
                    from( this.entityManager.cashMovements.fetchCollectionOnline(a) as Promise<PaginatedResponse<CashMovements>>),
                {
                    sort_type: "desc",
                    sort_col: "date",
                },
                'HistoryCashMovementsNormalizedData'
            ),
    };

    constructor() {
        subscribeInComponent(this.toolbarEventsService.events, (event) => {
            switch(event.type) {
                case 'toolbar.search':
                    return this.gridRef.onFilterTextBoxChanged(event.search);
                case 'grid.open_tool_panel':
                    return this.gridRef.openToolPanel(event.panel);
            }
        });
    }

    ngOnInit(): void {
        this.createToolbarButtons();
        this.setModuleTitle();
        this.toolbarEventsService.searchBarValue$.next("");
        this.toolbarEventsService.searchBarIcon$.next("tune");
        this.toolbarEventsService.searchBarIconFilled$.next("tune");
        this.toolbarEventsService.searchBarAction$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
        this.toolbarEventsService.searchBarActionFilled$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
        this.exportFormatterOptions = this.gridCellExportFormatterService.historyCashMovementsFormatter;
    }

    setModuleTitle() {
        this.toolbarEventsService.moduleTitle.next("HISTORY-CASH-MOVEMENTS");
    }

    actions({ action, data }: CliccableActionCashMovementscard) {
        switch (action) {
            case "details": {
                this.openDetail(data!);
                break;
            }
        }
    }

    async openDetail(cashMovement: CashMovements) {
        await this.cashMovementDialogService.openDialog({
            data: {
                cashMovement
            },
        });
    }

    rowClicked({ data, node }: RowClickedEvent) {
        this.openDetail(data);
    }

    createToolbarButtons() {
        this.toolbarEventsService.buttons$.next({
            barButtons: [
                {
                    isIt: () => this.connection.isOffline(),
                    name: "cloud_off",
                    icon: () => "cloud_off",
                    click: () => null,
                },
            ],
            panelButtons: [
                {
                    isIt: () => true,
                    name: "columns_panel",
                    icon: () => "view_week",
                    label: () => "TOPBAR.ACTIONS.COLUMN_SELECTED",
                    click: () => this.gridRef.openToolPanel('columns')
                },
                {
                    isIt: () => true,
                    name: "movable_columns",
                    icon: () => "drag_indicator",
                    label: () =>
                        this.movColumnsSwitch
                            ? "TOPBAR.ACTIONS.DISABLED_COLUMN_MOVEMENT"
                            : "TOPBAR.ACTIONS.ENABLED_COLUMN_MOVEMENT",
                    click: () => this.gridRef.columnMovableSwitch(this.movColumnsSwitch = !this.movColumnsSwitch)
                },
                {
                    isIt: () => true,
                    name: "save_columns_position",
                    icon: () => "save",
                    label: () => "TOPBAR.ACTIONS.SAVE_COLUMN_PREFERENCE",
                    click: () => this.gridRef.openDialogSaveColumnsPreference()
                },
                {
                    isIt: () => true,
                    name: "advanced_filter",
                    icon: () =>
                        this.isAdvancedFilterActive
                            ? "filter_alt_off"
                            : "filter_list_alt",
                    label: () =>
                        this.isAdvancedFilterActive
                            ? "TOPBAR.ACTIONS.ADVANCED_FILTERS_DISABLED"
                            : "TOPBAR.ACTIONS.ADVANCED_FILTERS_ENABLED",
                    click: () => this.gridRef.advancedFilterSwitch(this.isAdvancedFilterActive = !this.isAdvancedFilterActive)
                },
                {
                    isIt: () => true,
                    name: "export",
                    icon: () => "download",
                    label: () => 'TOPBAR.ACTIONS.EXPORT',
                    click: async () => {
                        const res = await this.gridDownloadDialogService.openDialog("history_cash_movements", {
                            data: this.tableData.map(tableData => ({
                                ...tableData,
                                sortFilterQuery: this.gridRef.getSortFilterQuery(),
                                countSelectedRows: this.gridRef.gridApi?.getSelectedRows().length
                            }))
                        }, 'cash_movements');

                        if (res) {
                            this.onExport(res, 'cash_movements');
                        }
                    }
                },
            ],
        });
    }

    @autobind
    onExport(dataExport: DataExport,tableToCall?:string) {
        this.gridRef?.onBtnExport(dataExport,tableToCall);
    }

    tableData: TableData[] = [
        {
            rowData$: null,
            dataType: new CashMovementsFe(),
            headersTranslate: headersTranslate.history_cash_movements,
            columnsFormatter: this.gridCellFormatterService.history_cash_movements,
        },
    ];
}
